import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import "../App.css";
import { toast } from "react-toastify";
import {
  Button,
  DatePicker,
  Empty,
  Popconfirm,
  Popover,
  Select,
  Tooltip,
} from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api from "../Interceptor";
import { getRoute } from "../api/routes";
import { setProjectList } from "../slices/app";
import { useSelector, useDispatch } from "react-redux";
import { CustomInput, ErrorText } from "../components/ui";
import CustomModal from "../components/customModal/customModal";
import ModalFooter from "../components/customModal/modalFooter";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  validateFormHandler,
  validateLiveField,
} from "../utils/validations/validateForm";

const { Option } = Select;
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8011";

const Projects = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const { projectList } = useSelector((state) => state.app);
  const [currencies, setCurrencies] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [isMilestoneModalOpen, setIsMilestoneModalOpen] = useState(false);
  const [editMilestone, setEditMilestone] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [expandedProjects, setExpandedProjects] = useState({});
  const [errors, setErrors] = useState({});
  const milestoneId = location.state?.milestoneId;
  const [updatedMilestoneData, setUpdatedMilestoneData] = useState({
    name: "",
    startDate: "",
    endDate: "",
  });
  const [hideNameField, setHideNameField] = useState(false);

  const handleAddMilestone = () => {
    setEditProject((prev) => ({
      ...prev,
      milestones: [
        ...prev.milestones,
        { name: "", startDate: "", endDate: "" },
      ],
    }));
  };

  const handleRemoveMilestone = (index) => {
    if (editProject.milestones.length <= 1) {
      toast.error("A project must have at least one milestone.");
      return;
    }
    const milestoneToRemove = editProject.milestones[index];

    const updatedMilestones = editProject.milestones.filter(
      (_, i) => i !== index,
    );
    setEditProject((prev) => ({
      ...prev,
      milestones: updatedMilestones,
      deletedMilestones: milestoneToRemove._id
        ? [...prev.deletedMilestones, milestoneToRemove._id]
        : prev.deletedMilestones || [],
    }));
  };

  const handleMilestoneChange = (index, field, value) => {
    const updatedMilestones = editProject.milestones.map((milestone, i) =>
      i === index ? { ...milestone, [field]: value } : milestone,
    );
    setEditProject((prev) => ({
      ...prev,
      milestones: updatedMilestones,
    }));
  };

  const toggleMilestones = (projectId) => {
    setExpandedProjects((prev) => ({
      ...prev,
      [projectId]: !prev[projectId],
    }));
  };

  const handleUpdateMilestoneInsideProject = async (milestone) => {
    try {
      setLoading(true);
      const updatedPayload = {
        id: milestone._id,
        name: milestone.name,
        startDate: milestone.startDate,
        endDate: milestone.endDate === "" ? null : milestone.endDate || null,
      };

      await api.patch(
        getRoute("users", "admin", "projects", false, false, "milestone"),
        updatedPayload,
      );
    } catch (error) {
      console.error("Error updating milestone inside project:", error);
      toast.error("Error updating milestone");
    } finally {
      setLoading(false);
    }
  };

  const getAllCurrencies = async () => {
    try {
      const { data: currencyData } = await api.get(
        `${API_BASE_URL}/api/v1/getAllCurrencies`,
      );
      setCurrencies(currencyData);
    } catch (error) {
      if (error?.isUnpaid) {
        return;
      }
      toast.error("Oops! Error while fetching Currencies");
    }
  };

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await api.get(
        getRoute("users", "admin", "projects", false, false, ""),
      );
      const data = response.data;
      if (Array.isArray(data)) {
        dispatch(setProjectList(response.data));
        setLoading(false);
      } else {
        throw new Error("Projects data is not in the expected format");
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to load projects";
      toast.error(errorMessage);
    }
  };

  useEffect(() => {
    getAllCurrencies();
  }, []);
  useEffect(() => {
    if (milestoneId && projectList.length > 0) {
      const projectWithMilestone = projectList.find((project) =>
        project.milestones.some((m) => m._id === milestoneId),
      );

      if (projectWithMilestone) {
        setExpandedProjects((prev) => ({
          ...prev,
          [projectWithMilestone._id]: true,
        }));
      }
    }
  }, [milestoneId, projectList]);

  const handleDelete = async (id) => {
    try {
      await api.delete(
        `${getRoute("users", "admin", "projects", false, false, "")}?id=${id}`,
      );
      toast.success("Project deleted successfully");
      fetchProjects();
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error("Failed to delete project");
    }
  };
  const handleDeleteMilestone = async (id, project) => {
    if (project.milestones.length <= 1) {
      toast.error("A project must have at least one milestone.");
      return;
    }

    try {
      await api.delete(
        `${getRoute(
          "users",
          "admin",
          "projects",
          false,
          false,
          "milestone",
        )}?id=${id}`,
      );
      toast.success("Milestone deleted successfully");
      fetchProjects();
    } catch (error) {
      console.error("Error deleting milestone:", error);
      toast.error("Failed to delete milestone");
    }
  };
  const handleDeleteInvoice = async (id) => {
    try {
      await api.delete(
        `${getRoute(
          "users",
          "admin",
          "projectInvoices",
          false,
          false,
          "",
        )}?id=${id}`,
      );
      toast.success("Invoice deleted successfully");
      fetchProjects();
    } catch (error) {
      console.error("Error deleting invoice:", error);
      toast.error("Failed to delete invoice");
    }
  };
  const validateProjectData = (project, editProject) => {
    const errors = {};
    const projectErrors = validateFormHandler(
      {
        name: project.project,
        currency: editProject?.currency?._id,
      },
      { name: "project" },
    );

    Object.assign(errors, projectErrors);
    const milestones = Array.isArray(editProject?.milestones)
      ? editProject.milestones
      : [];

    milestones.forEach((m, index) => {
      const nameKey = `milestoneName_${index}`;
      const startKey = `milestoneStartDate_${index}`;
      const endKey = `milestoneEndDate_${index}`;
      const milestoneNameErrors = validateFormHandler(
        { name: m.name || "" },
        { name: "project" },
      );

      if (milestoneNameErrors.name) {
        errors[nameKey] = milestoneNameErrors.name;
      }
      if (!m.startDate) {
        errors[startKey] = "Start date is required";
      }
      if (m.endDate) {
        const s = new Date(m.startDate);
        const e = new Date(m.endDate);
        if (isNaN(s) || isNaN(e)) {
          errors[endKey] = "Invalid date format";
        } else if (e < s) {
          errors[endKey] = "End date must be after start date";
        }
      }
    });

    return errors;
  };

  const handleUpdate = async (updatedProject) => {
    const combinedErrors = validateProjectData(updatedProject, editProject);

    if (Object.keys(combinedErrors).length > 0) {
      setErrors(combinedErrors);
      return;
    }

    const projectId = updatedProject._id;
    const selectedCurrencyId = editProject.currency._id;

    const oldMilestones = editProject.milestones.filter((m) => m._id);
    const newMilestones = editProject.milestones.filter((m) => !m._id);
    const newMilestoneValues = newMilestones.map((m) => ({
      name: m.name,
      startDate: m.startDate,
      endDate: m.endDate === "" ? null : m.endDate || null,
    }));

    const projectInList = projectList.find((p) => p._id === updatedProject._id);
    const isProjectChanged =
      updatedProject.project !== editProject.name ||
      updatedProject.description !== editProject.description ||
      updatedProject.clientName !== editProject.clientName ||
      updatedProject.clientCompany !== editProject.clientCompany ||
      selectedCurrencyId !== editProject.currency._id ||
      updatedProject.hasMilestone !== projectInList.hasMilestone;

    const milestoneChanged = oldMilestones.some((m) => {
      const original = projectInList?.milestones.find((x) => x._id === m._id);
      const normalize = (d) =>
        d ? new Date(d).toISOString().split("T")[0] : null;
      return (
        m.name !== original?.name ||
        normalize(m.startDate) !== normalize(original?.startDate) ||
        normalize(m.endDate) !== normalize(original?.endDate)
      );
    });
    const isMilestoneDeleted =
      editProject.deletedMilestones && editProject.deletedMilestones.length > 0;

    if (
      editProject.deletedMilestones &&
      editProject.deletedMilestones.length > 0
    ) {
      await Promise.all(
        editProject.deletedMilestones.map(async (id) => {
          try {
            await api.delete(
              `${getRoute(
                "users",
                "admin",
                "projects",
                false,
                false,
                "milestone",
              )}?id=${id}`,
            );
          } catch (err) {
            console.error("Failed to delete milestone:", err);
          }
        }),
      );
    }
    const isMilestoneAdded = newMilestones.length > 0;
    if (
      !isProjectChanged &&
      !isMilestoneAdded &&
      !milestoneChanged &&
      !isMilestoneDeleted
    ) {
      toast.info("No changes detected");
      closeModal();
      return;
    }
    try {
      setLoading(true);
      await Promise.all(
        oldMilestones.map(async (m, i) => {
          const original = projectList
            .find((p) => p._id === projectId)
            ?.milestones.find((x) => x._id === m._id);

          const normalize = (d) =>
            d ? new Date(d).toISOString().split("T")[0] : null;

          const changed =
            m.name !== original?.name ||
            normalize(m.startDate) !== normalize(original?.startDate) ||
            normalize(m.endDate) !== normalize(original?.endDate);

          if (changed) {
            await handleUpdateMilestoneInsideProject(m);
          }
        }),
      );
      const newMilestoneIds = await Promise.all(
        newMilestones.map(async (m) => {
          const res = await api.post(
            getRoute("users", "admin", "projects", false, false, "milestone"),
            {
              name: m.name,
              startDate: m.startDate,
              endDate: m.endDate || null,
            },
          );
          return res.data;
        }),
      );

      const allMilestoneIds = [
        ...oldMilestones.map((m) => m._id),
        ...newMilestoneIds,
      ];

      const payload = {
        name: updatedProject.project,
        currency: selectedCurrencyId,
        id: projectId,
        milestones: allMilestoneIds,
        hasMilestone: updatedProject.hasMilestone,
      };

      if (updatedProject.description?.trim()) {
        payload.description = updatedProject.description.trim();
      }
      if (updatedProject.clientName?.trim()) {
        payload.clientName = updatedProject.clientName.trim();
      }
      if (updatedProject.clientCompany?.trim()) {
        payload.clientCompany = updatedProject.clientCompany.trim();
      }

      await api.put(
        getRoute("users", "admin", "projects", false, false, ""),
        payload,
      );

      toast.success("Project and milestones updated successfully");
      fetchProjects();
      closeModal();
    } catch (error) {
      console.error("Error updating project:", error);
      toast.error("Error updating project");
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (project) => {
    setEditProject({
      ...project,
      newMilestones: [],
      deletedMilestones: [],
    });
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setErrors({});
    // setEditProject(null);
  };
  const handleNameChange = (e) => {
    const value = e.target.value;
    // setName(value);

    const error = validateLiveField("name", value, { name: "project" });

    setErrors((prev) => ({ ...prev, name: error || undefined }));
  };
  const validateMilestoneNameLive = (index, value) => {
    const error = validateLiveField("name", value, { name: "project" });

    setErrors((prev) => {
      const newErrors = { ...prev };

      if (error) {
        newErrors[`milestoneName_${index}`] = error;
      } else {
        delete newErrors[`milestoneName_${index}`];
      }

      return newErrors;
    });
  };

  const handleUpdateMilestone = async (updatedMilestone) => {
    const original = editMilestone;
    const validationErrors = {};
    const milestoneNameErrors = validateFormHandler(
      { name: updatedMilestone.name || "" },
      { name: "project" },
    );

    if (milestoneNameErrors.name) {
      validationErrors.milestoneName = milestoneNameErrors.name;
    }

    if (!updatedMilestone.startDate) {
      validationErrors.milestoneStartDate = "Start date is required";
    }
    if (updatedMilestone.endDate) {
      const start = new Date(updatedMilestone.startDate);
      const end = new Date(updatedMilestone.endDate);

      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        validationErrors.milestoneEndDate_0 = "Invalid date format";
      } else if (end < start) {
        validationErrors.milestoneEndDate_0 =
          "End date must be same or after start date";
      }
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    const normalizeDate = (date) =>
      date ? new Date(date).toISOString().split("T")[0] : null;

    const isChanged =
      updatedMilestone.name !== original.name ||
      normalizeDate(updatedMilestone.startDate) !==
        normalizeDate(original.startDate) ||
      normalizeDate(updatedMilestone.endDate) !==
        normalizeDate(original.endDate);

    if (!isChanged) {
      toast.info("No changes detected");
      closeMilestoneModal();
      return;
    }

    const updatedPayload = {
      id: updatedMilestone._id,
      name: updatedMilestone.name,
      startDate: updatedMilestone.startDate,
      endDate:
        updatedMilestone.endDate === ""
          ? null
          : updatedMilestone.endDate || null,
    };

    try {
      setLoading(true);
      await api.patch(
        getRoute("users", "admin", "projects", false, false, "milestone"),
        updatedPayload,
      );

      toast.success("Milestone updated successfully");
      fetchProjects();
      closeMilestoneModal();
    } catch (error) {
      toast.error("Error updating milestone");
    } finally {
      setLoading(false);
    }
  };

  const openMilestoneEditModal = (milestone, projectId) => {
    setEditMilestone({ ...milestone, _projectId: projectId });
    setUpdatedMilestoneData({
      name: milestone.name || "",
      startDate: milestone.startDate || "",
      endDate: milestone.endDate || "",
    });
    setIsMilestoneModalOpen(true);
  };
  const milestoneIdsForNoMilestoneProjects = projectList
    .filter((project) => project.hasMilestone === false) // only projects without milestones
    .flatMap((project) => project.milestones.map((milestone) => milestone._id));

  useEffect(() => {
    if (editMilestone?._id) {
      setHideNameField(
        milestoneIdsForNoMilestoneProjects.includes(editMilestone._id),
      );
    }
  }, [editMilestone, milestoneIdsForNoMilestoneProjects]);

  const closeMilestoneModal = () => {
    setIsMilestoneModalOpen(false);
    setEditMilestone(null);
    setUpdatedMilestoneData({});
    setErrors({});
  };
  const handleOpenProjectDetail = (
    id,
    name,
    description,
    clientName,
    clientCompany,
  ) => {
    navigate("/project", {
      state: {
        projectId: id,
        projectName: name,
        description,
        clientName,
        clientCompany,
      },
    });
  };
  const createProjectMilestoneInvoice = async (
    projectId,
    currencyName,
    milestoneId,
  ) => {
    const currencyObj = currencies.find((c) => c.name === currencyName);

    if (!currencyObj) {
      toast.error("Invalid currency selected");
      return;
    }

    const payload = {
      date: new Date(),
      project: projectId,
      currency: currencyObj._id,
      milestones: [milestoneId],
    };

    try {
      const endpoint = getRoute(
        "users",
        "admin",
        "projectInvoices",
        false,
        true,
      );

      const response = await api.post(endpoint, payload);
      fetchProjects();

      if (response.status === 201) {
        toast.success("Invoice created successfully!");
      }
    } catch (error) {
      console.error("Error:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to create invoice. Please try again.";
      toast.error(errorMessage);
    }
  };
  const createProjectInvoice = async (projectId, currencyName) => {
    // ✅ Find the matching currency object
    const currencyObj = currencies.find((c) => c.name === currencyName);

    // ✅ If currency not found, show an error and stop
    if (!currencyObj) {
      toast.error("Invalid currency selected");
      return;
    }

    const payload = {
      date: new Date(),
      project: projectId,
      currency: currencyObj._id,
    };

    try {
      const endpoint = getRoute(
        "users",
        "admin",
        "projectInvoices",
        false,
        true,
      );

      const response = await api.post(endpoint, payload);
      fetchProjects();

      if (response.status === 201) {
        toast.success("Invoice created successfully!");
      }
    } catch (error) {
      console.error("Error:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to create invoice. Please try again.";
      toast.error(errorMessage);
    }
  };
  const handleViewInvoice = (id) => {
    navigate("/projectInvoice", {
      state: { invoiceId: id, editMode: false },
    });
  };

  const handleEditInvoice = (id) => {
    navigate("/projectInvoice", {
      state: { invoiceId: id, editMode: true },
    });
  };

  // Calculate summary statistics
  const totalProjects = projectList.length;
  const totalMilestones = projectList
    .filter((project) => project.hasMilestone)
    .reduce((acc, project) => acc + project.milestones.length, 0);

  const totalInvoices = projectList.reduce(
    (acc, project) => acc + project.invoices.length,
    0,
  );
  const activeProjects = projectList.filter((project) =>
    project.milestones.some(
      (milestone) => new Date(milestone.endDate || new Date()) >= new Date(),
    ),
  ).length;
  return (
    <div className="min-h-screen flex flex-col p-3 gap-2 overflow-hidden  font-customFont">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
        <div style={{ fontFamily: "Noto Serif, sans-serif" }}>
          <h1 className="font-bold text-lg md:text-xl">Projects</h1>
          <p className="text-xs text-gray-500 font-semibold">
            Manage your projects, milestones and invoices
          </p>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {/* Total Projects Card */}
        <div className="group relative overflow-hidden border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-blue-100 shadow-lg  transition-all duration-300 rounded-xl p-4 pb-3 flex flex-col">
          <div className="absolute top-0 right-0 w-32 h-32 bg-blue-200 rounded-full -mr-16 -mt-16 opacity-20"></div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-md">
                <i className="fas fa-project-diagram text-white text-md"></i>
              </div>
              <p className="text-blue-700 text-sm font-semibold uppercase tracking-wide">
                Projects
              </p>
            </div>
          </div>
          <p className="text-blue-900 text-3xl font-bold mt-1 relative z-10">
            {totalProjects}
          </p>
          <p className="text-blue-600 text-xs mt-2 font-medium">
            Total projects created
          </p>
        </div>

        {/* Active Projects Card */}
        <div className="group relative overflow-hidden border border-green-200 bg-gradient-to-br from-green-50 via-white to-green-100 shadow-lg transition-all duration-300 rounded-xl p-4 pb-3 flex flex-col">
          <div className="absolute top-0 right-0 w-32 h-32 bg-green-200 rounded-full -mr-16 -mt-16 opacity-20"></div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center shadow-md">
                <i className="fas fa-play-circle text-white text-md"></i>
              </div>
              <p className="text-green-700 text-sm font-semibold uppercase tracking-wide">
                Active
              </p>
            </div>
          </div>
          <p className="text-green-900 text-3xl font-bold mt-1 relative z-10">
            {activeProjects}
          </p>
          <p className="text-green-600 text-xs mt-2 font-medium">
            Currently running projects
          </p>
        </div>

        {/* Total Milestones Card */}
        <div className="group relative overflow-hidden border border-purple-200 bg-gradient-to-br from-purple-50 via-white to-purple-100 shadow-lg  transition-all duration-300 rounded-xl p-4 pb-3 flex flex-col ">
          <div className="absolute top-0 right-0 w-32 h-32 bg-purple-200 rounded-full -mr-16 -mt-16 opacity-20"></div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center shadow-md">
                <i className="fas fa-flag text-white text-md"></i>
              </div>
              <p className="text-purple-700 text-sm font-semibold uppercase tracking-wide">
                Milestones
              </p>
            </div>
          </div>
          <p className="text-purple-900 text-3xl font-bold mt-1 relative z-10">
            {totalMilestones}
          </p>
          <p className="text-purple-600 text-xs mt-2 font-medium">
            Total project milestones
          </p>
        </div>

        {/* Total Invoices Card */}
        <div className="group relative overflow-hidden border border-red-200 bg-gradient-to-br from-red-50 via-white to-red-100 shadow-lg transition-all duration-300 rounded-xl p-4 pb-3 flex flex-col">
          <div className="absolute top-0 right-0 w-32 h-32 bg-red-200 rounded-full -mr-16 -mt-16 opacity-20"></div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center shadow-md">
                <i className="fas fa-file-invoice text-white text-md"></i>
              </div>
              <p className="text-red-700 text-sm font-semibold uppercase tracking-wide">
                Invoices
              </p>
            </div>
          </div>
          <p className="text-red-900 text-3xl font-bold mt-1 relative z-10">
            {totalInvoices}
          </p>
          <p className="text-red-600 text-xs mt-2 font-medium">
            Generated invoices
          </p>
        </div>
      </div>

      <div className="overflow-x-auto">
        {loading ? (
          <div className="flex justify-center items-center h-48 mt-20">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 items-start">
            {projectList.length === 0 ? (
              <div className="col-span-full bg-white border border-gray-200 rounded-xl py-12 px-6 text-center shadow-sm">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-project-diagram text-gray-400 text-2xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  No Projects Yet
                </h3>
                <p className="text-gray-500">
                  Create your first project to start tracking milestones and
                  invoices.
                </p>
              </div>
            ) : (
              projectList.map((project) => {
                const completedMilestones = project.milestones.filter(
                  (milestone) =>
                    milestone.endDate &&
                    new Date(milestone.endDate) < new Date(),
                ).length;
                const progressPercentage =
                  project.milestones.length > 0
                    ? Math.round(
                        (completedMilestones / project.milestones.length) * 100,
                      )
                    : 0;

                return (
                  <div
                    key={project._id}
                    className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden"
                  >
                    {/* Card Header */}
                    <div className="p-4 sm:p-6 border-b border-gray-100">
                      <div className="flex items-start justify-between mb-4">
                        <div
                          className="flex-1 cursor-pointer group"
                          onClick={() =>
                            handleOpenProjectDetail(
                              project._id,
                              project.name,
                              project.description,
                              project.clientName,
                              project.clientCompany,
                            )
                          }
                        >
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                              <i className="fas fa-project-diagram text-blue-600"></i>
                            </div>
                            <div>
                              <h3 className="font-semibold text-gray-800 group-hover:text-blue-600 transition-colors">
                                {project.name}
                              </h3>
                              <p className="text-sm text-gray-500">
                                {project.currency.symbol}{" "}
                                {project.currency.name}
                              </p>
                            </div>
                          </div>

                          {(project.clientName || project.clientCompany) && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <i className="fas fa-user text-xs"></i>
                              <span>
                                {project.clientName}
                                {project.clientName &&
                                  project.clientCompany &&
                                  " - "}
                                {project.clientCompany}
                              </span>
                            </div>
                          )}
                          {project.description && (
                            <p className="text-sm text-gray-500 line-clamp-2 mt-3">
                              {project.description}
                            </p>
                          )}
                        </div>

                        {/* Expand/Collapse Button */}
                        <button
                          className={`ml-4 w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                            expandedProjects[project._id]
                              ? "border-blue-500 bg-blue-500 text-white rotate-180"
                              : "border-gray-300 bg-white text-gray-400 hover:border-blue-500 hover:text-blue-500"
                          }`}
                          onClick={() => toggleMilestones(project._id)}
                        >
                          <i className="fas fa-chevron-down text-sm"></i>
                        </button>
                      </div>

                      {/* Progress Bar */}
                      <div className="mb-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium text-gray-700">
                            Progress
                          </span>
                          <span className="text-sm text-gray-500">
                            {progressPercentage}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${progressPercentage}%` }}
                          ></div>
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="grid grid-cols-2 gap-4 mb-4 border-t pt-2">
                        <div className="text-center">
                          <p className="text-lg font-bold text-gray-800">
                            {project.hasMilestone
                              ? project.milestones.length
                              : 0}
                          </p>
                          <p className="text-xs text-gray-500">Milestones</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-bold text-gray-800">
                            {project.invoices.length}
                          </p>
                          <p className="text-xs text-gray-500">Invoices</p>
                        </div>
                        {/* <div className="text-center">
                          <p className="text-lg font-bold text-gray-800">
                            {completedMilestones}
                          </p>
                          <p className="text-xs text-gray-500">Completed</p>
                        </div> */}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2">
                        <button
                          className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
                          onClick={() => openEditModal(project)}
                        >
                          <i className="fas fa-edit mr-2"></i>
                          Edit
                        </button>

                        <Tooltip title="Create Project Invoice">
                          <button
                            className="bg-green-50 hover:bg-green-100 text-green-600 rounded-lg px-3 py-2 text-sm transition-colors"
                            onClick={() =>
                              createProjectInvoice(
                                project._id,
                                project.currency.name,
                              )
                            }
                          >
                            <i className="fas fa-file-invoice"></i>
                          </button>
                        </Tooltip>

                        <Popconfirm
                          title="Are you sure you want to delete this project?"
                          onConfirm={() => handleDelete(project._id)}
                          okText="Yes"
                          cancelText="No"
                        >
                          <button className="bg-red-50 hover:bg-red-100 text-red-600 rounded-lg px-3 py-2 text-sm transition-colors">
                            <i className="fas fa-trash"></i>
                          </button>
                        </Popconfirm>
                      </div>
                    </div>

                    {/* Expandable Section */}
                    {expandedProjects[project._id] && (
                      <div className="px-4 sm:px-6 pb-4 sm:pb-6 animate-fadeIn">
                        {/* Milestones Section */}
                        <div className="mb-6">
                          <div className="flex items-center justify-between my-2">
                            <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                              <i className="fas fa-flag text-purple-600"></i>
                              {project.hasMilestone === true
                                ? "Milestones"
                                : "Project Details"}
                            </h4>
                            <span className="text-sm text-gray-500">
                              {project.hasMilestone
                                ? project.milestones.length
                                : ""}{" "}
                              {/* {project.milestones.length === 1
                                ? 'item'
                                : 'items'} */}
                            </span>
                          </div>

                          <div className="space-y-3">
                            {project.milestones.length > 0 ? (
                              project.milestones.map((milestone, index) => {
                                const isCompleted =
                                  milestone.endDate &&
                                  new Date(milestone.endDate) < new Date();
                                const isActive =
                                  milestone.startDate &&
                                  new Date(milestone.startDate) <= new Date() &&
                                  (!milestone.endDate ||
                                    new Date(milestone.endDate) >= new Date());

                                return (
                                  <div
                                    key={milestone._id}
                                    className={`p-4 rounded-lg border-l-4 ${
                                      isCompleted
                                        ? "border-green-500 bg-green-50"
                                        : isActive
                                          ? "border-blue-500 bg-blue-50"
                                          : "border-gray-300 bg-gray-50"
                                    }`}
                                  >
                                    <div className="flex justify-between items-start">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                          <h5 className="font-medium text-gray-800">
                                            {milestone.name}
                                          </h5>
                                          <span
                                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                                              isCompleted
                                                ? "bg-green-100 text-green-700"
                                                : isActive
                                                  ? "bg-blue-100 text-blue-700"
                                                  : "bg-gray-100 text-gray-700"
                                            }`}
                                          >
                                            {isCompleted
                                              ? "Completed"
                                              : isActive
                                                ? "Active"
                                                : "Pending"}
                                          </span>
                                        </div>
                                        <div className="flex items-center gap-4 text-sm text-gray-600">
                                          <div className="flex items-center gap-1">
                                            <i className="fas fa-calendar-alt text-xs"></i>
                                            <span>
                                              Start:{" "}
                                              {new Date(
                                                milestone.startDate,
                                              ).toLocaleDateString()}
                                            </span>
                                          </div>
                                          <div className="flex items-center gap-1">
                                            <i className="fas fa-calendar-check text-xs"></i>
                                            <span>
                                              End:{" "}
                                              {milestone.endDate
                                                ? new Date(
                                                    milestone.endDate,
                                                  ).toLocaleDateString()
                                                : "Not set"}
                                            </span>
                                          </div>
                                        </div>
                                      </div>

                                      <div className="flex gap-2 ml-4">
                                        <button
                                          className="w-8 h-8 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg flex items-center justify-center transition-colors"
                                          onClick={() =>
                                            openMilestoneEditModal(milestone)
                                          }
                                        >
                                          <i className="fas fa-edit text-xs"></i>
                                        </button>
                                        {project.hasMilestone && (
                                          <Tooltip title="Create Milestone Invoice">
                                            <button
                                              className="w-8 h-8 bg-green-100 hover:bg-green-200 text-green-600 rounded-lg flex items-center justify-center transition-colors"
                                              onClick={() =>
                                                createProjectMilestoneInvoice(
                                                  project._id,
                                                  project.currency.name,
                                                  milestone._id,
                                                )
                                              }
                                            >
                                              <i className="fas fa-file-invoice text-xs"></i>
                                            </button>
                                          </Tooltip>
                                        )}

                                        {project.milestones.length > 1 && (
                                          <Popconfirm
                                            title="Delete milestone?"
                                            onConfirm={() =>
                                              handleDeleteMilestone(
                                                milestone._id,
                                                project,
                                              )
                                            }
                                            okText="Yes"
                                            cancelText="No"
                                          >
                                            <button className="w-8 h-8 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg flex items-center justify-center transition-colors">
                                              <i className="fas fa-trash text-xs"></i>
                                            </button>
                                          </Popconfirm>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="text-center py-8 text-gray-500">
                                <i className="fas fa-flag text-2xl mb-2"></i>
                                <p>No milestones available</p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Invoices Section */}
                        <div>
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                              <i className="fas fa-file-invoice text-orange-600"></i>
                              Invoices
                            </h4>
                            <span className="text-sm text-gray-500">
                              {project.invoices.length}{" "}
                              {project.invoices.length === 1
                                ? "invoice"
                                : "invoices"}
                            </span>
                          </div>

                          <div className="space-y-3">
                            {project.invoices.length > 0 ? (
                              project.invoices.map((invoice) => (
                                <div
                                  key={invoice._id}
                                  className="p-4 bg-orange-50 border border-orange-200 rounded-lg"
                                >
                                  <div className="flex justify-between items-center">
                                    <div>
                                      <h5 className="font-medium text-gray-800 mb-1">
                                        Invoice #{invoice.number}
                                      </h5>
                                      <div className="flex items-center gap-1 text-sm text-gray-600">
                                        <i className="fas fa-calendar text-xs"></i>
                                        <span>
                                          {new Date(
                                            invoice.date,
                                          ).toLocaleDateString()}
                                        </span>
                                      </div>
                                    </div>

                                    <div className="flex gap-2">
                                      <Tooltip title="View Invoice">
                                        <button
                                          className="w-8 h-8 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg flex items-center justify-center transition-colors"
                                          onClick={() =>
                                            handleViewInvoice(invoice._id)
                                          }
                                        >
                                          <i className="fas fa-eye text-xs"></i>
                                        </button>
                                      </Tooltip>

                                      <Tooltip title="Edit Invoice">
                                        <button
                                          className="w-8 h-8 bg-green-100 hover:bg-green-200 text-green-600 rounded-lg flex items-center justify-center transition-colors"
                                          onClick={() =>
                                            handleEditInvoice(invoice._id)
                                          }
                                        >
                                          <i className="fas fa-edit text-xs"></i>
                                        </button>
                                      </Tooltip>

                                      <Popconfirm
                                        title="Delete invoice?"
                                        onConfirm={() =>
                                          handleDeleteInvoice(invoice._id)
                                        }
                                        okText="Yes"
                                        cancelText="No"
                                      >
                                        <Tooltip title="Delete Invoice">
                                          <button className="w-8 h-8 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg flex items-center justify-center transition-colors">
                                            <i className="fas fa-trash text-xs"></i>
                                          </button>
                                        </Tooltip>
                                      </Popconfirm>
                                    </div>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="text-center py-8 text-gray-500">
                                <i className="fas fa-file-invoice text-2xl mb-2"></i>
                                <p>No invoices available</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      <CustomModal
        isOpen={isModalOpen}
        onClose={closeModal}
        modalTitle="Edit Project"
      >
        {editProject && (
          <form
            onSubmit={(e) => {
              e.preventDefault();

              const formData = new FormData(e.target);
              const updatedProject = {
                ...editProject,
                project: formData.get("project"),
                description: formData.get("description"),
                clientName: formData.get("clientName"),
                clientCompany: formData.get("clientCompany"),
                startDate: formData.get("startDate"),
                endDate: formData.get("endDate"),
                currency: formData.get("currency"),
                milestones: editProject.milestones,
                hasMilestone: editProject.hasMilestone,
                newMilestones: editProject.newMilestones,
              };
              handleUpdate(updatedProject);
            }}
            className="space-y-4"
          >
            <div className="overflow-y-auto scrollbar-hidden max-h-[50vh]">
              <div>
                <CustomInput
                  type="text"
                  name="project"
                  label="Title"
                  defaultValue={editProject.name}
                  // required
                  message={errors.name}
                  onChange={handleNameChange}
                  requir="true"
                />
              </div>
              <CustomInput
                type="text"
                name="currency"
                defaultValue={editProject.currency.symbol}
                label="Project Currency"
                requir="true"
                readOnly
                // disabled
                // className="bg-gray-100"
              />
              <div>
                <CustomInput
                  type="text"
                  name="description"
                  label="Description"
                  defaultValue={editProject.description}
                  // required
                  // message={errors.name}
                  onChange={(e) => {
                    if (errors.description) {
                      setErrors((prev) => ({
                        ...prev,
                        description: undefined,
                      }));
                    }
                  }}
                />
              </div>
              <div>
                <CustomInput
                  type="text"
                  name="clientName"
                  label="Client Name"
                  defaultValue={editProject.clientName}
                  // required
                  // message={errors.name}
                  onChange={(e) => {
                    if (errors.clientName) {
                      setErrors((prev) => ({ ...prev, clientName: undefined }));
                    }
                  }}
                />
              </div>
              <div>
                <CustomInput
                  type="text"
                  name="clientCompany"
                  label="Client Company"
                  defaultValue={editProject.clientCompany}
                  // required
                  // message={errors.name}
                  onChange={(e) => {
                    if (errors.clientCompany) {
                      setErrors((prev) => ({
                        ...prev,
                        clientCompany: undefined,
                      }));
                    }
                  }}
                />
              </div>
              <>
                {projectList.map((pro) => {
                  // Only render checkbox if hasMilestone is false
                  if (
                    pro.hasMilestone === false &&
                    pro._id === editProject._id
                  ) {
                    return (
                      <div
                        key={pro.id}
                        className="mb-4 flex items-center justify-between mr-1"
                      >
                        <CustomInput
                          type="checkbox"
                          label=" Milestones"
                          checked={editProject.hasMilestone}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setEditProject((prev) => ({
                              ...prev,
                              hasMilestone: isChecked,
                              // Optionally, handle default milestones here
                            }));
                          }}
                          className="cursor-pointer"
                        />
                      </div>
                    );
                  }

                  // Return null if condition not met
                  return null;
                })}
              </>
              {editProject.hasMilestone === true ? (
                <>
                  <div className="flex justify-between items-center">
                    <label className="bg-themeGradient text-white rounded-r-full pl-1 pr-2 py-1">
                      Milestones
                    </label>
                    <div
                      className="bg-[#034ae4] text-white flex justify-center items-center w-[20px] h-[20px] rounded-full cursor-pointer"
                      onClick={handleAddMilestone}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </div>
                  </div>
                  {editProject.milestones.map((milestone, index) => (
                    <div
                      key={index}
                      className="relative mt-4 px-3 py-5 border border-gray-200 rounded-lg shadow-sm bg-[#f9fcfd]"
                    >
                      <CustomInput
                        type="text"
                        value={milestone.name}
                        onChange={(e) => {
                          const value = e.target.value;
                          handleMilestoneChange(index, "name", value);
                          validateMilestoneNameLive(index, value);
                        }}
                        placeholder="Enter milestone name"
                        // disabled={!!milestone._id}
                        message={errors[`milestoneName_${index}`]}
                      />

                      <div className="flex gap-2 w-full mt-1">
                        <span className="w-1/2">
                          <label
                            htmlFor={`milestone-startDate-${index}`}
                            className="block font-medium mb-1 text-gray-700"
                          >
                            Start Date{" "}
                            {errors[`milestoneStartDate_${index}`] && (
                              <span className="text-red-500">*</span>
                            )}
                          </label>

                          <DatePicker
                            id={`milestone-startDate-${index}`}
                            className="w-full h-[42px] rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-themeColor focus:border-themeColor"
                            value={
                              milestone.startDate
                                ? dayjs(milestone.startDate)
                                : null
                            }
                            placeholder="Enter Milestone Start Date"
                            disabledDate={(current) =>
                              milestone.endDate
                                ? current && current > dayjs(milestone.endDate)
                                : false
                            }
                            onChange={(value) => {
                              const selectedDate = value
                                ? value.format("YYYY-MM-DD")
                                : "";
                              handleMilestoneChange(
                                index,
                                "startDate",
                                selectedDate,
                              );
                              setErrors((prev) => {
                                const newErrors = { ...prev };
                                delete newErrors[`milestoneStartDate_${index}`];
                                return newErrors;
                              });
                            }}
                          />

                          {errors[`milestoneStartDate_${index}`] && (
                            <ErrorText
                              message={errors[`milestoneStartDate_${index}`]}
                            />
                          )}
                        </span>
                        <span className="w-1/2">
                          <div>
                            <label
                              htmlFor={`milestone-endDate-${index}`}
                              className="block font-medium mb-1 text-gray-700"
                            >
                              End Date{" "}
                              {errors.endDate && (
                                <span className="text-red-500">*</span>
                              )}
                            </label>

                            <DatePicker
                              id={`milestone-endDate-${index}`}
                              className="w-full h-[42px] rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-themeColor focus:border-themeColor"
                              value={
                                milestone.endDate
                                  ? dayjs(milestone.endDate)
                                  : null
                              }
                              placeholder="Enter Milestone End Date"
                              disabledDate={(current) =>
                                milestone.startDate
                                  ? current &&
                                    current < dayjs(milestone.startDate)
                                  : false
                              }
                              onChange={(value) => {
                                const selectedDate = value
                                  ? value.format("YYYY-MM-DD")
                                  : "";
                                handleMilestoneChange(
                                  index,
                                  "endDate",
                                  selectedDate,
                                );
                                if (errors.endDate) {
                                  setErrors((prev) => ({
                                    ...prev,
                                    endDate: undefined,
                                  }));
                                }
                              }}
                            />

                            {errors.endDate && (
                              <ErrorText message={errors.endDate} />
                            )}
                          </div>
                        </span>
                      </div>
                      {editProject.milestones.length > 1 && (
                        <Button
                          type="text"
                          onClick={(e) => handleRemoveMilestone(index)}
                          className="!p-0 !m-0 !h-auto !min-h-0 !leading-none absolute top-[-1px] right-[-1px]"
                        >
                          <span className="flex items-center justify-center w-4 h-4 rounded-full bg-red-500/15 border border-red-500">
                            <DeleteOutlined className="text-red-600 text-[10px]" />
                          </span>
                        </Button>
                      )}
                    </div>
                  ))}
                </>
              ) : null}
            </div>
            <ModalFooter onCancel={closeModal} isLoading={loading} />
          </form>
        )}
      </CustomModal>
      <CustomModal
        isOpen={isMilestoneModalOpen}
        onClose={closeMilestoneModal}
        modalTitle="Edit Milestone"
      >
        {editMilestone && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const updatedMilestone = {
                ...editMilestone,
                ...updatedMilestoneData,
              };
              handleUpdateMilestone(updatedMilestone);
            }}
            className="space-y-4"
          >
            {!hideNameField && (
              <CustomInput
                type="text"
                name="name"
                label="Name"
                value={
                  updatedMilestoneData.name !== undefined
                    ? updatedMilestoneData.name
                    : editMilestone.name
                }
                onChange={(e) => {
                  setUpdatedMilestoneData((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }));

                  if (errors.milestoneName) {
                    setErrors((prev) => ({
                      ...prev,
                      milestoneName: undefined,
                    }));
                  }
                }}
                // required
                className="border border-gray-300 rounded-md p-2 w-full focus:outline-none focus:ring-themeColor focus:border-themeColor"
                message={errors.milestoneName}
                requir="true"
              />
            )}
            <div className="flex gap-2 w-full mt-1">
              <span className="w-1/2">
                <label
                  htmlFor="milestone-startDate"
                  className="block font-medium mb-1 text-gray-700"
                >
                  Start Date{" "}
                  {errors.milestoneStartDate && (
                    <span className="text-red-500">*</span>
                  )}
                </label>

                <DatePicker
                  className="w-full h-[42px] rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-themeColor focus:border-themeColor"
                  value={
                    updatedMilestoneData.startDate
                      ? dayjs(updatedMilestoneData.startDate)
                      : null
                  }
                  placeholder="Enter Milestone Start Date"
                  disabledDate={(current) =>
                    updatedMilestoneData.endDate
                      ? current && current > dayjs(updatedMilestoneData.endDate)
                      : false
                  }
                  onChange={(value) => {
                    const selectedDate = value
                      ? value.format("YYYY-MM-DD")
                      : "";
                    setUpdatedMilestoneData((prev) => ({
                      ...prev,
                      startDate: selectedDate,
                    }));

                    if (errors.milestoneStartDate) {
                      setErrors((prev) => ({
                        ...prev,
                        milestoneStartDate: undefined,
                      }));
                    }
                  }}
                />
                {errors.milestoneStartDate && (
                  <ErrorText message={errors.milestoneStartDate} />
                )}
              </span>
              <span className="w-1/2">
                <label
                  htmlFor="milestone-startDate"
                  className="block font-medium mb-1 text-gray-700"
                >
                  End Date{" "}
                  {errors.endDate && <span className="text-red-500">*</span>}
                </label>
                <DatePicker
                  className="w-full h-[42px] rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-themeColor focus:border-themeColor"
                  value={
                    updatedMilestoneData.endDate
                      ? dayjs(updatedMilestoneData.endDate)
                      : null
                  }
                  placeholder="Enter Milestone End Date"
                  disabledDate={(current) =>
                    updatedMilestoneData.startDate
                      ? current &&
                        current < dayjs(updatedMilestoneData.startDate)
                      : false
                  }
                  onChange={(value) => {
                    const selectedDate = value
                      ? value.format("YYYY-MM-DD")
                      : "";
                    setUpdatedMilestoneData((prev) => ({
                      ...prev,
                      endDate: selectedDate,
                    }));

                    if (errors.milestoneEndDate_0) {
                      setErrors((prev) => ({
                        ...prev,
                        milestoneEndDate_0: undefined,
                      }));
                    }
                  }}
                />
              </span>
            </div>
            <ModalFooter onCancel={closeMilestoneModal} isLoading={loading} />
          </form>
        )}
      </CustomModal>
    </div>
  );
};

export default Projects;
